import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Heart, 
  Leaf, 
  Sparkles, 
  CheckCircle2, 
  Users, 
  Phone, 
  Mail, 
  ArrowRight, 
  Shield, 
  Sun, 
  Compass, 
  TreePine, 
  Target, 
  HelpCircle, 
  Clock, 
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import useDocumentMeta from '../hooks/useDocumentMeta';

export default function LocalEsbjerg() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqData = {
    "@type": "FAQPage",
    "@context": "https://schema.org",
    "mainEntity": [
      {
        "name": "Hvad betyder beskyttet beskæftigelse efter servicelovens § 103?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Beskyttet beskæftigelse er et tilpasset beskæftigelsestilbud til voksne under folkepensionsalderen, som ikke kan arbejde på almindelige vilkår på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale udfordringer. Betegnelserne serviceloven 103, paragraf 103 og beskyttet beskæftigelse 103 henviser til den lovbestemmelse, som danner rammen om tilbuddet. Hos LIVSLYST tilpasses opgaver og støtte til den enkelte borgers ressourcer, interesser og dagsform.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvem kan få beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Målgruppen kan blandt andet være voksne med kognitive udfordringer, udviklingsforstyrrelser, autismespektrumforstyrrelser, erhvervet hjerneskade eller særlige sociale problemer. Hos LIVSLYST indledes der en dialog om borgerens støttebehov, interesser og trivsel for at vurdere, om det lille arbejdsfællesskab er det rette match.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan bliver man visiteret til beskyttet beskæftigelse?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Det er borgerens bopælskommune, der foretager en konkret vurdering og bevilger et tilbud efter servicelovens § 103. Borgere og pårørende kan derfor kontakte kommunen om visitation, mens sagsbehandlere og andre fagpersoner kan kontakte LIVSLYST for en dialog om målgruppe, støttebehov, match og aktuelle pladser.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvilke arbejdsopgaver kan indgå i et beskyttet beskæftigelsesforløb?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST kan arbejdsdagen bestå af dyrepleje, havearbejde, vedligeholdelse af grønne områder, naturbaserede opgaver og kreative aktiviteter. Der er også fælles pauser, oplevelser og socialt samvær. Opgaverne har en tydelig funktion og bliver afstemt efter borgerens behov, interesser og dagsform.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan bliver arbejdsdagen tilpasset den enkelte borger?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST arbejder med faste rutiner, tydelig kommunikation, visuel støtte og individuelt tilpassede krav. Den anerkendende tilgang og brugen af Low Arousal skal skabe ro og forebygge unødig stress. Udviklingen følges gennem individuelle mål, handleplaner, trivselssamtaler og løbende evaluering med fokus på personlige, sociale og praktiske færdigheder.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvordan kan arbejde med dyr og natur støtte udvikling og trivsel?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Arbejdet med dyr og natur kan skabe struktur, ansvar, omsorg og en oplevelse af at være nødvendig. Hos LIVSLYST bruges miljøet som en pædagogisk ramme, og opgaverne vælges sammen med borgeren. Det kan understøtte læring, arbejdsidentitet, deltagelse og større selvstændighed i et overskueligt tempo.",
          "@type": "Answer"
        }
      },
      {
        "name": "Hvor stort er fællesskabet i et beskyttet beskæftigelsestilbud?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "LIVSLYST har plads til seks borgere og tilbyder derfor et lille og overskueligt arbejdsfællesskab. Den lille ramme giver mulighed for genkendelighed, trygge relationer og tæt individuelt nærvær. Tilbuddet er relevant for borgere og fagpersoner, der søger beskyttet beskæftigelse nær Esbjerg i rolige og naturskønne omgivelser.",
          "@type": "Answer"
        }
      },
      {
        "name": "Kan man besøge et beskyttet beskæftigelsestilbud før opstart?",
        "@type": "Question",
        "acceptedAnswer": {
          "text": "Hos LIVSLYST er det muligt at kontakte tilbuddet for at høre mere eller aftale en rundvisning. Et besøg kan give borgeren, de pårørende og kommunen et bedre grundlag for at vurdere, om de små rammer, arbejdsopgaverne, dyrene og naturen passer til den enkeltes behov.",
          "@type": "Answer"
        }
      }
    ]
  };

  useDocumentMeta(
    'Beskyttet beskæftigelse nær Esbjerg | Se mulighederne',
    'Oplev beskyttet beskæftigelse nær Esbjerg i trygge rammer med dyr, natur og individuelt tilpassede opgaver. Kontakt LIVSLYST i Brørup i dag.',
    ''
  );

  const criteriaList = [
    'Borgerens behov for struktur, forudsigelighed og pædagogisk støtte',
    'Interesse for praktiske arbejdsopgaver med dyr og natur',
    'Muligheden for at deltage i et lille og trygt arbejdsfællesskab',
    'En kommunal bevilling til beskyttet beskæftigelse efter paragraf 103'
  ];

  const workActivities = [
    {
      title: 'Dyrepleje',
      desc: 'Dyrepleje med faste rutiner, omsorg og praktisk ansvar.',
      icon: Heart
    },
    {
      title: 'Havearbejde',
      desc: 'Havearbejde og vedligeholdelse af grønne områder.',
      icon: TreePine
    },
    {
      title: 'Kreativt værksted',
      desc: 'Kreative værkstedsopgaver med mulighed for fordybelse.',
      icon: Sparkles
    },
    {
      title: 'Fællesskab & pauser',
      desc: 'Fælles pauser, oplevelser og socialt samvær.',
      icon: Users
    }
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      
      {/* 1. HERO / HEADER SECTION */}
      <section className="bg-gradient-to-b from-stone-100 to-stone-50 py-16 md:py-24 border-b border-stone-200 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center space-x-2 text-xs md:text-sm text-stone-500 mb-2" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-emerald-700 transition-colors">Forside</Link>
            <span>/</span>
            <span className="text-emerald-800 font-medium">Esbjerg</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-900 text-xs font-semibold uppercase tracking-wider"
          >
            <MapPin className="w-3.5 h-3.5 text-emerald-700" />
            <span>Beskyttet beskæftigelse § 103 · For borgere nær Esbjerg</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight"
          >
            Beskyttet beskæftigelse nær Esbjerg med plads til den enkelte
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed font-normal"
          >
            Hos <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST</Link> tilbyder vi beskyttet beskæftigelse nær Esbjerg til voksne, som har brug for en rolig, overskuelig og individuelt tilrettelagt arbejdsdag. Vores tilbud ligger i Brørup, hvor et lille fællesskab, praktiske opgaver og tæt støtte skaber plads til trivsel og udvikling.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-2 flex flex-wrap justify-center gap-4 text-sm"
          >
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl shadow-xs transition-colors"
            >
              <span>Kontakt os om pladser</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a 
              href="tel:51504654"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors shadow-xs"
            >
              <Phone className="w-4 h-4 text-emerald-700" />
              <span>Ring på 51 50 46 54</span>
            </a>
          </motion.div>

          {/* Quick Trust Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 max-w-3xl mx-auto border-t border-stone-200/80 text-left">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Users className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Maks. 6 borgere</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Leaf className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Dyr & naturmiljø</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Shield className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Servicelovens § 103</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-700 bg-white/80 p-2.5 rounded-lg border border-stone-200">
              <Compass className="w-4 h-4 text-emerald-700 shrink-0" />
              <span>Brørup (nær Esbjerg)</span>
            </div>
          </div>

        </div>
      </section>

      {/* 2. SECTION: ET LILLE BESKÆFTIGELSESTILBUD I BRØRUP TÆT PÅ ESBJERG */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <motion.div 
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Compass className="w-4 h-4" />
                <span>Geografi & Fællesskab</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Et lille beskæftigelsestilbud i Brørup tæt på Esbjerg
              </h2>

              <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                Vores adresse er i Brørup, og vi har ikke en afdeling i Esbjerg. Til gengæld er tilbuddet relevant at undersøge for borgere fra Esbjerg og området omkring byen, når der ønskes et mindre privat beskæftigelsestilbud med dyr, natur og tydelig struktur i hverdagen.
              </p>

              <p className="text-stone-600 leading-relaxed text-base">
                Tilbuddet er indrettet til seks borgere. Det lille arbejdsfællesskab giver gode muligheder for genkendelighed, nærvær og støtte, som tager udgangspunkt i den enkelte. Vi tilpasser opgaver og krav efter borgerens ressourcer, interesser, støttebehov og dagsform.
              </p>

              <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 text-sm sm:text-base">
                    Beliggenhed i rolige rammer
                  </h4>
                  <p className="text-stone-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Surhavevej 14 A, 6650 Brørup – med nem kørsel og gode transportmuligheder fra hele Esbjerg-området.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right Media Card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-lg border border-stone-200 bg-stone-100">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/img-7376_1.jpeg" 
                  alt="Beskyttet beskæftigelse med dyr og natur hos LIVSLYST" 
                  className="w-full h-80 sm:h-96 object-cover"
                  loading="lazy"
                />
                <div className="p-6 bg-stone-900 text-white">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-1">
                    <Leaf className="w-4 h-4" />
                    <span>Natur & Dyr</span>
                  </div>
                  <p className="text-stone-300 text-sm leading-relaxed">
                    Maksimalt seks borgere sikrer et trygt, uforstyrret og individuelt tilpasset lærings- og arbejdsmiljø.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 3. SECTION: HVEM ER BESKYTTET BESKÆFTIGELSE EFTER § 103 TIL */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <Target className="w-4 h-4" />
              <span>Målgruppe & Visitation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Hvem er beskyttet beskæftigelse efter § 103 til
            </h2>
            <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
              Når der søges efter serviceloven 103, handler det om kommunens forpligtelse til at tilbyde beskyttet beskæftigelse til borgere under folkepensionsalderen, som opfylder lovens betingelser. Det kan være personer med betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer.
            </p>
            <p className="text-stone-600 leading-relaxed text-base">
              Beskyttet beskæftigelse 103 bliver ofte brugt som en kort betegnelse for beskæftigelse efter servicelovens § 103. Tilbuddet kan være relevant, når en borger ikke kan opnå eller fastholde arbejde på almindelige vilkår. Det er altid borgerens bopælskommune, som vurderer målgruppen og træffer afgørelse om bevilling.
            </p>
            <p className="text-stone-600 leading-relaxed text-base">
              Når vi vurderer, om LIVSLYST kan være det rette match, ser vi både på borgerens behov, motivation og interesser. Dialogen kan foregå sammen med borgeren, pårørende, en sagsbehandler eller andre relevante fagpersoner, så rammerne og forventningerne er tydelige fra begyndelsen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {criteriaList.map((criterion, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-stone-200/80 shadow-xs flex items-start gap-4 hover:border-emerald-300 transition-colors"
              >
                <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800 shrink-0 mt-0.5">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <p className="text-stone-800 font-medium text-base sm:text-lg leading-snug">
                  {criterion}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. SECTION: HVAD KAN EN ARBEJDSDAG MED DYR OG NATUR INDEHOLDE */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <Sun className="w-4 h-4" />
              <span>Opgaver & Trivsel</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad kan en arbejdsdag med dyr og natur indeholde
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Hos os er dyr og natur en integreret del af arbejdsdagen. Opgaverne er konkrete og har et tydeligt formål, så borgeren kan opleve ansvar og deltagelse. Indholdet bliver tilpasset individuelt, og målet er at skabe realistiske succesoplevelser uden at stille uoverskuelige krav.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {workActivities.map((act, i) => {
              const IconComp = act.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-stone-50 rounded-2xl p-6 border border-stone-200 flex flex-col justify-between hover:shadow-md transition-shadow"
                >
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center shadow-xs">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-stone-900">{act.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{act.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="p-8 rounded-3xl bg-emerald-50/70 border border-emerald-200/80 max-w-4xl mx-auto text-stone-800">
            <p className="text-base sm:text-lg leading-relaxed text-emerald-950 font-normal">
              Arbejdet med dyr kan understøtte struktur, ansvarsfølelse og oplevelsen af at være nødvendig. Naturen giver samtidig mulighed for bevægelse, sanseoplevelser og varierede udendørsopgaver. Den enkelte deltager på et niveau, som passer til egne forudsætninger og den aktuelle dagsform.
            </p>
          </div>
        </div>
      </section>

      {/* 5. SECTION: INDIVIDUEL STØTTE GØR ARBEJDSDAGEN MERE OVERSKUELIG */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Shield className="w-4 h-4" />
                <span>Pædagogisk Støtte</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Individuel støtte gør arbejdsdagen mere overskuelig
              </h2>
              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Beskyttet beskæftigelse skal give mening for den enkelte. Derfor arbejder vi med faste rutiner, tydelig kommunikation og tilpassede forventninger. Borgeren bliver så vidt muligt inddraget i valg af opgaver og mål, så arbejdsdagen understøtter medbestemmelse, motivation og størst mulig selvstændighed.
              </p>
              
              <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-xs">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/fullsizerender3.jpeg" 
                  alt="Have og naturaktiviteter hos LIVSLYST" 
                  className="w-full h-56 object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <div className="p-8 rounded-3xl bg-white border border-stone-200 shadow-xs space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                  Fra personlige interesser til realistiske mål
                </h3>
                
                <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                  Vi tager udgangspunkt i det, borgeren allerede kan, og det, som vækker interesse. Udvikling foregår i eget tempo gennem overskuelige opgaver og løbende støtte. Der arbejdes med individuelle mål, handleplaner, trivselssamtaler og evaluering, så indsatsen kan justeres undervejs.
                </p>

                <div className="p-6 rounded-2xl bg-amber-50/60 border border-amber-200/80">
                  <h4 className="font-bold text-amber-950 text-base mb-2">Low Arousal & anerkendende tilgang</h4>
                  <p className="text-stone-700 leading-relaxed text-sm sm:text-base">
                    Vores tilgang er anerkendende og omsorgsfuld. Low Arousal bruges til at forebygge stress og konflikter gennem rolig kontakt, tilpassede krav og tydelige rammer. Målet er ikke at presse udviklingen frem, men at skabe betingelser, hvor borgeren kan deltage og opbygge færdigheder.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ ACCORDION SECTION */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-800 text-xs md:text-sm font-semibold tracking-wide shadow-xs mb-4">
              <HelpCircle className="w-4 h-4 text-emerald-700" />
              <span>Spørgsmål & Svar</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mb-4">
              Ofte stillede spørgsmål om beskyttet beskæftigelse
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              Find svar på ofte stillede spørgsmål om vores tilbud, visitation, arbejdsopgaver og rammer for borgere nær Esbjerg.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Venstre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqData.mainEntity.slice(0, Math.ceil(faqData.mainEntity.length / 2)).map((item, index) => {
                const actualIndex = index;
                const isOpen = openFaq === actualIndex;
                return (
                  <div
                    key={actualIndex}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-stone-50/80 border-emerald-300 shadow-xs'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-hidden"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug pr-2">
                        {item.name}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen ? 'bg-emerald-700 text-white rotate-180' : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-200/60 mt-1">
                        <p>{item.acceptedAnswer.text}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Højre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqData.mainEntity.slice(Math.ceil(faqData.mainEntity.length / 2)).map((item, idx) => {
                const actualIndex = idx + Math.ceil(faqData.mainEntity.length / 2);
                const isOpen = openFaq === actualIndex;
                return (
                  <div
                    key={actualIndex}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-stone-50/80 border-emerald-300 shadow-xs'
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-hidden"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug pr-2">
                        {item.name}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen ? 'bg-emerald-700 text-white rotate-180' : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-200/60 mt-1">
                        <p>{item.acceptedAnswer.text}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 6. SECTION: SÅDAN BEGYNDER ET FORLØB EFTER PARAGRAF 103 */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <HelpCircle className="w-4 h-4" />
              <span>Opstart & Samarbejde</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Sådan begynder et forløb efter <Link to="/paragraf-103" className="font-semibold text-inherit hover:text-emerald-800 transition-colors">paragraf 103</Link>
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
              Et forløb hos LIVSLYST kræver, at borgeren er bevilget beskyttet beskæftigelse af sin bopælskommune. Herefter kan kommunen tage kontakt for at drøfte målgruppe, støttebehov og match. Pårørende og borgeren selv er også velkomne til at kontakte os og høre mere om hverdagen.
            </p>
          </div>

          <div className="p-8 sm:p-10 rounded-3xl bg-stone-50 border border-stone-200 shadow-xs space-y-6">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              Er LIVSLYST det rette match for borgeren
            </h3>
            
            <p className="text-stone-700 leading-relaxed text-base sm:text-lg">
              Det er vigtigt, at både borgeren og kommunen har et klart billede af tilbuddet. Derfor lægger vi vægt på en åben dialog om behov, interesser og forventninger. En rundvisning kan give et konkret indtryk af rammerne, arbejdsopgaverne, dyrene og det lille fællesskab i Brørup.
            </p>

            <p className="text-stone-700 leading-relaxed text-base sm:text-lg">
              Søger du beskyttet beskæftigelse nær Esbjerg, er du velkommen til at kontakte os for en uforpligtende samtale. Vi fortæller gerne mere om vores tilgang og aktuelle pladsforhold, så borgeren, de pårørende og kommunen får et godt grundlag for at vurdere det videre forløb.
            </p>

            <div className="pt-4 flex flex-wrap gap-4">
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl transition-colors shadow-xs"
              >
                <span>Kontakt os for en rundvisning</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="tel:51504654"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors shadow-xs"
              >
                <Phone className="w-4 h-4 text-emerald-700" />
                <span>51 50 46 54</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* 7. OTHER LOCAL REGIONS QUICK LINKS */}
      <section className="py-12 bg-stone-100 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-stone-900">Beskyttet beskæftigelse i naboområder</h3>
              <p className="text-sm text-stone-600">Se vores tilbud målrettet andre nærliggende byer</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/beskyttet-beskaeftigelse-vejen"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 text-sm font-medium transition-colors"
              >
                <span>Beskyttet beskæftigelse nær Vejen</span>
                <ChevronRight className="w-4 h-4 text-emerald-700" />
              </Link>
              <Link
                to="/beskyttet-beskaeftigelse-kolding"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 text-sm font-medium transition-colors"
              >
                <span>Beskyttet beskæftigelse nær Kolding</span>
                <ChevronRight className="w-4 h-4 text-emerald-700" />
              </Link>
              <Link
                to="/paragraf-103"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
              >
                <span>Læs alt om § 103</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8. BOTTOM CTA CONTACT SECTION */}
      <section className="py-16 md:py-24 bg-stone-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-900/80 border border-emerald-700 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                <Phone className="w-3.5 h-3.5" />
                <span>Kontakt LIVSLYST ApS</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Ønsker du at høre mere om pladser eller booke en rundvisning?
              </h2>

              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Vi tager os god tid til en uforpligtende samtale og svarer på alle spørgsmål om målgruppe, pædagogik og visitation.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-950/50"
                >
                  <span>Gå til kontaktsiden</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:51504654"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-stone-800 hover:bg-stone-700 text-stone-200 border border-stone-700 font-medium rounded-xl transition-colors"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span>Ring 51 50 46 54</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 bg-stone-800/80 backdrop-blur-sm p-8 rounded-3xl border border-stone-700/80 space-y-6">
              <h3 className="text-lg font-bold text-white border-b border-stone-700 pb-4">
                Praktisk information
              </h3>
              
              <div className="space-y-4 text-sm text-stone-300">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">Adresse:</span>
                    Surhavevej 14 A, 6650 Brørup
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">Telefon:</span>
                    <a href="tel:51504654" className="hover:text-emerald-400 transition-colors">51 50 46 54</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-white block">E-mail:</span>
                    <a href="mailto:kontakt@livslyst-aps.dk" className="hover:text-emerald-400 transition-colors">kontakt@livslyst-aps.dk</a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
